import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { FolderOpen, ExternalLink, RefreshCw, HardDrive, FileIcon, Clock } from "lucide-react";
import { Spinner, formatTimeAgo, formatFileSize } from "./CommonComponents";
import { useItemDetailsContext } from "./ItemDetailsContext";

const ItemDetailsGoogleDriveSection = () => {
    const {
        itemType,
        itemData,
        gdriveFolderId,
        driveFiles,
        lastSyncedAt,
        driveSyncing,
        creatingDriveFolder,
        handleCreateDriveFolder,
        handleSyncDriveFiles,
    } = useItemDetailsContext();

    if (itemType !== "folder") return null;

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <HardDrive className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">Google Drive</span>
            </div>

            {!gdriveFolderId ? (
                <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/40 border border-border/50">
                    <FolderOpen className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground flex-1">No folder linked</span>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                onClick={() => handleCreateDriveFolder(itemData.id)}
                                disabled={creatingDriveFolder}
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                            >
                                {creatingDriveFolder ? (
                                    <Spinner size={10} />
                                ) : (
                                    <FolderOpen className="h-3 w-3" />
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {creatingDriveFolder ? "Creating..." : "Link Google Drive folder"}
                        </TooltipContent>
                    </Tooltip>
                </div>
            ) : (
                <>
                    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/40 border border-border/50">
                        <span className="text-xs text-muted-foreground">
                            {driveFiles.length} file{driveFiles.length !== 1 ? 's' : ''}
                        </span>
                        {lastSyncedAt && !driveSyncing && (
                            <>
                                <div className="h-4 w-px bg-border/50" />
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatTimeAgo(lastSyncedAt)}
                                </span>
                            </>
                        )}
                        <div className="flex-1" />
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => handleSyncDriveFiles(itemData.id)}
                                    disabled={driveSyncing}
                                >
                                    <RefreshCw className={cn(
                                        "h-3.5 w-3.5",
                                        driveSyncing && "animate-spin"
                                    )} />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Refresh files</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(
                                            `https://drive.google.com/drive/folders/${gdriveFolderId}`,
                                            "_blank"
                                        );
                                    }}
                                >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Open in Google Drive</TooltipContent>
                        </Tooltip>
                    </div>
                    
                    {driveFiles.length > 0 && (
                        <Card className="py-0 overflow-hidden">
                            <CardContent className="p-0">
                                <div className="divide-y">
                                    {driveFiles.map((file, index) => (
                                        <div
                                            key={file.id}
                                            className={cn(
                                                "group flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors",
                                                "hover:bg-accent/50"
                                            )}
                                            onClick={() => window.open(file.web_view_link, "_blank")}
                                        >
                                            {file.icon_link ? (
                                                <img
                                                    src={file.icon_link}
                                                    alt=""
                                                    className="w-8 h-8 shrink-0"
                                                />
                                            ) : (
                                                <div className="w-8 h-8 rounded bg-muted flex items-center justify-center shrink-0">
                                                    <FileIcon className="h-4 w-4 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                                                    {file.name}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    {formatFileSize(file.size)} • Modified{" "}
                                                    {new Date(file.modified_time).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default ItemDetailsGoogleDriveSection;
